import * as yup from "yup";
import i18next from "config/i18next";
import { AnyObject } from "yup/lib/types";
import { TierType } from "./tiersForm/tiersTable/types";
import { TIERS_ERRORS } from "utils/constants";

const requiredMessage = i18next.t("validations:required");

const requiredPositiveNumber = yup
  .number()
  .test(
    "positiveNumber",
    i18next.t("validations:positiveNumber"),
    (value) => !!(value && value > 0)
  )
  .required(requiredMessage);

const requiredPositivePorcentage = yup
  .number()
  .test(
    "percentageValidation",
    i18next.t("polygonPricing:percentageValidation"),
    (value) => !!(value! <= 100)
  )
  .test(
    "positiveNumber",
    i18next.t("validations:positiveNumber"),
    (value) => !!(value && value > 0)
  )
  .required(requiredMessage);

const validateBase = (
  value: TierType[],
  context: yup.TestContext<AnyObject>,
  name: string
) => {
  const { createError } = context;
  let previousBase: number;
  let errorIndex: number = 0;
  value.some((tier, index) => {
    if (tier.base) {
      if (previousBase && tier.base <= previousBase) {
        errorIndex = index;
        return true;
      } else {
        previousBase = tier.base;
      }
    }
    return false;
  });
  if (errorIndex > 0)
    return createError({
      path: `tiers.${name}[${errorIndex}].base`,
      message: TIERS_ERRORS.GENERIC_ERROR,
    });
  return true;
};

const validatePositive = (value: TierType[], name: string) => {
  let errors: yup.ValidationError[] = [];
  value.forEach(({ base, price }, index) => {
    if (index !== 0) {
      if (base === 0) {
        errors.push(
          new yup.ValidationError(
            TIERS_ERRORS.POSITIVE_ERROR,
            null,
            `tiers.${name}[${index}].base`
          )
        );
      }
      if (price !== null && price < 0) {
        errors.push(
          new yup.ValidationError(
            TIERS_ERRORS.POSITIVE_ERROR,
            null,
            `tiers.${name}[${index}].price`
          )
        );
      }
    }
  });

  if (errors && errors.length > 0) {
    return new yup.ValidationError(errors);
  }
  return true;
};

const tierArray = (name: string) =>
  yup
    .array(
      yup.object({
        base: yup.number().nullable(true).required(TIERS_ERRORS.REQUIRED_ERROR),
        price: yup
          .number()
          .nullable(true)
          .required(TIERS_ERRORS.REQUIRED_ERROR),
      })
    )
    .test("positiveNumbers", (value) =>
      validatePositive(value as TierType[], name)
    )
    .test("greaterThanPrevious", (value, context) =>
      validateBase(value as TierType[], context, name)
    );

export const validationSchema = (assuredEnabled: boolean) =>
  yup.object({
    price: yup.object({
      pickUp: requiredPositiveNumber,
      extraPickUpKm: requiredPositiveNumber,
      delivered: requiredPositiveNumber,
      extraDeliveredKm: requiredPositiveNumber,
      extraReturnedKm: requiredPositiveNumber,
      returned: requiredPositiveNumber,
      rainBonus: requiredPositiveNumber,
    }),
    assured: yup.object({
      amountPerHour: assuredEnabled
        ? requiredPositiveNumber
        : yup.number().required(requiredMessage),
      presenteeism: assuredEnabled
        ? requiredPositivePorcentage
        : yup
            .number()
            .test(
              "percentageValidation",
              i18next.t("polygonPricing:percentageValidation"),
              (value) => !!(value! <= 100)
            )
            .required(requiredMessage),
      minimumPercentage: yup
        .number()
        .test(
          "percentageValidation",
          i18next.t("polygonPricing:percentageValidation"),
          (value) => !!(value! <= 100)
        )
        .required(requiredMessage),
      minimumAmount: yup.number().required(requiredMessage),
    }),
    tiers: yup.object({
      day: tierArray("day"),
      week: tierArray("week"),
    }),
  });
